import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { SessionStorageService } from '../../services/session-storage.service';
import { PosmDeliverySchedule } from '../models/posm-execution.models';
import { PosmService } from '../posm.service';

type ScheduleFilters = {
    keyword: string;
    retailer: string[];
    status: string | null;
    dueStatus: string | null;
};

@Component({
    selector: 'app-posm-delivery-schedule-list',
    standalone: true,
    imports: [
        CommonModule,
        DatePipe,
        FormsModule,
        ButtonModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputTextModule,
        MultiSelectModule,
        SelectModule,
        TagModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageSearchPanelComponent,
        PageDataPanelComponent
    ],
    template: `
        <app-page-shell>
            <app-page-hero
                eyebrow="POSM"
                title="Delivery Schedules"
                subtitle="Company-wide schedule tracking across projects, stores, transport releases, shipments, and deliveries.">
                <div actions>
                    <p-button label="Projects" icon="pi pi-briefcase" severity="secondary" [outlined]="true" (click)="goToProjects()"></p-button>
                    <p-button label="Refresh" icon="pi pi-refresh" (click)="loadSchedules()"></p-button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

            <app-page-search-panel title="Search Delivery Schedules" [activeFilters]="activeFilterCount()" [collapsed]="true" (clear)="clearFilters()" (search)="noop()">
                <div class="grid p-fluid">
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input pInputText [(ngModel)]="filters().keyword" (ngModelChange)="updateFilter('keyword', $event)" class="w-full">
                                <label>Schedule, project, store, item, truck</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-multiSelect [options]="retailerOptions()" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" [showToggleAll]="false" [ngModel]="filters().retailer" (ngModelChange)="updateFilter('retailer', $event)" selectedItemsLabel="{0} retailers selected" styleClass="w-full"></p-multiSelect>
                                <label>Retailer / Store Chain</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-2">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-chart-line"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select [options]="statusOptions()" optionLabel="label" optionValue="value" [showClear]="true" [(ngModel)]="filters().status" (ngModelChange)="updateFilter('status', $event)" styleClass="w-full"></p-select>
                                <label>Status</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-2">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-clock"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select [options]="dueStatusOptions()" optionLabel="label" optionValue="value" [showClear]="true" [(ngModel)]="filters().dueStatus" (ngModelChange)="updateFilter('dueStatus', $event)" styleClass="w-full"></p-select>
                                <label>Due Status</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-search-panel>

            <app-page-data-panel title="Schedule Directory" subtitle="Resolved delivery schedule records." [resultText]="filteredSchedules().length + ' of ' + schedules().length + ' schedules'">
                @if (loading()) {
                    <div class="schedule-empty">Loading delivery schedules.</div>
                } @else if (!filteredSchedules().length) {
                    <div class="schedule-empty">No delivery schedules found.</div>
                } @else {
                    <div class="schedule-card-grid">
                        @for (row of filteredSchedules(); track row.id || row.scheduleNumber) {
                            <article class="schedule-card">
                                <div class="card-top">
                                    <div class="card-title">
                                        <strong>{{ row.scheduleNumber || ('Schedule #' + row.id) }}</strong>
                                        <span>{{ row.projectNumber || '-' }} | {{ row.clientName || '-' }}</span>
                                    </div>
                                    <div class="card-tags">
                                        <p-tag [value]="coverageModeLabel(row)" [severity]="coverageModeSeverity(row)"></p-tag>
                                        <p-tag [value]="row.progressStage || row.status || 'PENDING'" [severity]="statusSeverity(row.progressStage || row.status)"></p-tag>
                                        <p-tag [value]="row.dueStatus || 'UNSCHEDULED'" [severity]="dueSeverity(row.dueStatus)"></p-tag>
                                    </div>
                                </div>

                                <div class="project-line">
                                    <span>Project</span>
                                    <strong>{{ row.projectName || '-' }}</strong>
                                </div>

                                @if (row.sourceWarehouseName) {
                                    <div class="warehouse-line">
                                        <i class="pi pi-warehouse"></i>
                                        <span>Source Warehouse</span>
                                        <strong>{{ row.sourceWarehouseName }}</strong>
                                    </div>
                                }

                                <div class="card-main">
                                    <div>
                                        <i class="pi pi-map-marker"></i>
                                        <span>
                                            <span class="identity-field">
                                                <small>{{ retailerSourceLabel(row) }}</small>
                                                <strong>{{ retailerLabel(row) }}</strong>
                                            </span>
                                            <span class="identity-field">
                                                <small>Store Location</small>
                                                <strong>{{ locationLabel(row) }}</strong>
                                                <small>{{ row.storeNumber || row.location?.externalLocationReference || '-' }} | {{ row.city || '-' }}</small>
                                            </span>
                                        </span>
                                    </div>
                                    <div>
                                        <i class="pi pi-box"></i>
                                        <span>
                                            <strong>{{ row.inventoryItem?.itemName || row.standName || '-' }}</strong>
                                            <small>Item {{ row.inventoryItem?.itemNumber || '-' }} | SKU {{ row.inventoryItem?.skuNumber || '-' }} | Size {{ row.standSize || '-' }} | Qty {{ row.standQty || row.projectItem?.quantity || 0 }}</small>
                                        </span>
                                    </div>
                                </div>

                                <div class="card-facts">
                                    <div>
                                        <span>Planned</span>
                                        <strong>{{ row.plannedInstallationDate | date:'dd MMM yyyy' }}</strong>
                                    </div>
                                    <div>
                                        <span>Release</span>
                                        <strong>{{ row.releaseNumber || 'No release' }}</strong>
                                    </div>
                                    <div>
                                        <span>Truck</span>
                                        <strong>{{ row.truckName || '-' }}</strong>
                                    </div>
                                    <div>
                                        <span>Driver</span>
                                        <strong>{{ row.driverName || '-' }}</strong>
                                    </div>
                                </div>

                                <div class="card-actions">
                                    <p-button label="Open Details" icon="pi pi-arrow-right" size="small" (click)="open(row)"></p-button>
                                </div>
                            </article>
                        }
                    </div>
                }
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .schedule-card-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.85rem;
            padding: 0.85rem;
            border: 1px solid #dbe5f1;
            border-radius: 8px;
            background: #eef3f8;
        }

        .schedule-card {
            display: grid;
            gap: 0.75rem;
            min-width: 0;
            border: 1px solid #cfdbea;
            border-left: 4px solid var(--primary-color);
            border-radius: 8px;
            padding: 0.9rem;
            background: #ffffff;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07);
        }

        .card-top {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 0.75rem;
        }

        .card-title,
        .project-line,
        .warehouse-line,
        .card-main span {
            min-width: 0;
        }

        .card-title strong,
        .card-title span,
        .project-line strong,
        .warehouse-line strong,
        .card-main strong,
        .card-main small,
        .card-facts strong {
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .card-title span,
        .project-line span,
        .warehouse-line span,
        .card-main small,
        .card-facts span {
            color: var(--text-color-secondary);
            font-size: 0.78rem;
        }

        .card-tags {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex-wrap: wrap;
            gap: 0.35rem;
        }

        .project-line,
        .warehouse-line,
        .card-main > div,
        .card-facts > div {
            border: 1px solid #d8e2ef;
            border-radius: 6px;
            background: #f8fafc;
            padding: 0.6rem;
        }

        .project-line strong,
        .warehouse-line strong,
        .card-facts strong {
            margin-top: 0.2rem;
        }

        .warehouse-line {
            align-items: center;
            column-gap: 0.6rem;
            display: grid;
            grid-template-columns: auto auto minmax(0, 1fr);
        }

        .warehouse-line i {
            color: var(--primary-color);
        }

        .card-main {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.6rem;
        }

        .card-main > div {
            display: flex;
            align-items: flex-start;
            gap: 0.55rem;
        }

        .card-main i {
            color: var(--primary-color);
            margin-top: 0.15rem;
        }

        .identity-field {
            display: block;
        }

        .identity-field + .identity-field {
            margin-top: 0.55rem;
            padding-top: 0.55rem;
            border-top: 1px solid #d8e2ef;
        }

        .card-facts {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 0.55rem;
        }

        .card-actions {
            display: flex;
            justify-content: flex-end;
        }

        .schedule-empty {
            border: 1px dashed #cbd5e1;
            border-radius: 8px;
            color: var(--text-color-secondary);
            padding: 2rem;
            text-align: center;
            background: #ffffff;
        }

        @media (max-width: 768px) {
            .schedule-card-grid,
            .card-main,
            .card-facts {
                grid-template-columns: 1fr;
            }

            .card-top {
                align-items: stretch;
                flex-direction: column;
            }

            .card-tags,
            .card-actions {
                justify-content: flex-start;
            }
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmDeliveryScheduleListComponent implements OnInit {
    private posmService = inject(PosmService);
    private sessionStorage = inject(SessionStorageService);
    private router = inject(Router);

    schedules = signal<PosmDeliverySchedule[]>([]);
    loading = signal(false);
    owningCompanyId = signal<number | null>(null);
    filters = signal<ScheduleFilters>({ keyword: '', retailer: [], status: null, dueStatus: null });

    filteredSchedules = computed(() => {
        const filters = this.filters();
        const query = `${filters.keyword || ''}`.trim().toLowerCase();
        return this.schedules().filter(row => {
            const haystack = [
                row.scheduleNumber,
                row.projectNumber,
                row.projectName,
                row.clientName,
                this.retailerLabel(row),
                row.location?.name,
                row.storeName,
                row.storeNumber,
                row.city,
                row.inventoryItem?.itemName,
                row.sourceWarehouseName,
                row.standName,
                row.truckName,
                row.driverName
            ].filter(Boolean).join(' ').toLowerCase();
            return (!query || haystack.includes(query))
                && (!filters.retailer.length || filters.retailer.includes(this.retailerFilterValue(row)))
                && (!filters.status || `${row.progressStage || row.status || ''}` === filters.status)
                && (!filters.dueStatus || `${row.dueStatus || ''}` === filters.dueStatus);
        });
    });

    statusOptions = computed(() => this.optionList(this.schedules().map(row => row.progressStage || row.status)));
    dueStatusOptions = computed(() => this.optionList(this.schedules().map(row => row.dueStatus)));
    retailerOptions = computed(() => {
        const options = new Map<string, { label: string; value: string }>();
        this.schedules().forEach(row => {
            const value = this.retailerFilterValue(row);
            const label = this.retailerLabel(row);
            if (value && label !== '-') options.set(value, { label, value });
        });
        return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
    });

    ngOnInit(): void {
        const companyId = this.sessionStorage.getCustomer()?.company?.id || this.sessionStorage.getCompany()?.id;
        this.owningCompanyId.set(companyId || null);
        this.loadSchedules();
    }

    loadSchedules(): void {
        const companyId = this.owningCompanyId();
        if (!companyId) return;
        this.loading.set(true);
        this.posmService.getAllDeliverySchedules(companyId).subscribe({
            next: rows => {
                this.schedules.set((rows || []).map(row => this.normalizeDates(row)));
                this.loading.set(false);
            },
            error: () => {
                this.schedules.set([]);
                this.loading.set(false);
            }
        });
    }

    metrics(): PageMetric[] {
        const rows = this.schedules();
        const ready = rows.filter(row => row.readinessStatus === 'READY').length;
        const released = rows.filter(row => !!row.transportReleaseId).length;
        const delivered = rows.filter(row => `${row.progressStage || row.status || ''}`.toUpperCase() === 'DELIVERED').length;
        return [
            { label: 'Schedules', value: rows.length, hint: 'company-wide', tone: 'info' },
            { label: 'Ready', value: ready, hint: 'eligible for release', tone: ready ? 'success' : 'info' },
            { label: 'Released', value: released, hint: 'order release linked', tone: released ? 'success' : 'info' },
            { label: 'Delivered', value: delivered, hint: 'completed rows', tone: delivered ? 'success' : 'info' }
        ];
    }

    activeFilterCount(): number {
        const filters = this.filters();
        return [filters.keyword, filters.retailer.length, filters.status, filters.dueStatus].filter(Boolean).length;
    }

  updateFilter(key: keyof ScheduleFilters, value: any): void {
        this.filters.update(current => ({ ...current, [key]: key === 'retailer' ? (value || []) : (value || null) }));
    }

    coverageModeLabel(row: PosmDeliverySchedule): string {
        return `${row.coverageMode || ''}`.toUpperCase() === 'LOGICAL' ? 'Logical Coverage' : 'Physical Reservation';
    }

    coverageModeSeverity(row: PosmDeliverySchedule): 'info' | 'secondary' {
        return `${row.coverageMode || ''}`.toUpperCase() === 'LOGICAL' ? 'info' : 'secondary';
    }

    clearFilters(): void {
        this.filters.set({ keyword: '', retailer: [], status: null, dueStatus: null });
    }

    noop(): void {}

    open(row: PosmDeliverySchedule): void {
        if (row.id) {
            this.router.navigate(['/posm/executions/delivery-schedules', row.id]);
        }
    }

    goToProjects(): void {
        this.router.navigate(['/posm/executions']);
    }

    statusSeverity(status?: string | null): string {
        const value = `${status || ''}`.toUpperCase();
        if (value.includes('DELIVERED') || value.includes('COMPLETE') || value.includes('APPROVED')) return 'success';
        if (value.includes('ERROR') || value.includes('EXCEPTION') || value.includes('REJECT')) return 'danger';
        if (value.includes('PENDING') || value.includes('SCHEDULED')) return 'warning';
        return 'info';
    }

    dueSeverity(status?: string | null): string {
        const value = `${status || ''}`.toUpperCase();
        if (value === 'OVERDUE') return 'danger';
        if (value === 'DUE_TODAY') return 'warning';
        return 'info';
    }

    retailerLabel(row: PosmDeliverySchedule): string {
        return row.location?.retailer?.name || row.customerName || '-';
    }

    locationLabel(row: PosmDeliverySchedule): string {
        return row.location?.name || row.storeName || row.storeNumber || '-';
    }

    retailerSourceLabel(row: PosmDeliverySchedule): string {
        return row.location?.retailer?.name ? 'Retailer / Store Chain' : 'Imported Customer Name';
    }

    private retailerFilterValue(row: PosmDeliverySchedule): string {
        if (row.location?.retailer?.id) return `RETAILER_ID:${row.location.retailer.id}`;
        const importedName = `${row.customerName || ''}`.trim().replace(/\s+/g, ' ').toLowerCase();
        return importedName ? `TEXT:${importedName}` : '';
    }

    private optionList(values: Array<string | null | undefined>) {
        return Array.from(new Set(values.map(value => `${value || ''}`.trim()).filter(Boolean)))
            .sort((a, b) => a.localeCompare(b))
            .map(value => ({ label: value, value }));
    }

    private normalizeDates(row: PosmDeliverySchedule): PosmDeliverySchedule {
        return {
            ...row,
            plannedInstallationDate: this.toDate(row.plannedInstallationDate),
            actualDeliveryDate: this.toDate(row.actualDeliveryDate),
            statusDate: this.toDate(row.statusDate)
        };
    }

    private toDate(value: unknown): Date | undefined {
        if (!value) return undefined;
        const date = value instanceof Date ? value : new Date(value as string);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }
}
